import type { Key } from './i18n'
import type { Sale } from './mock-data'
import { saleRails } from './cash'

/**
 * 140 — what was unusual about a sale (`sales.sync_flags`). create_sale writes
 * it; NULL is an ordinary sale. Every screen reads it through this module, so
 * the till, the history, the Z and the morning report say the same thing.
 *
 *   offline  it came through the device's queue (replayed later)
 *   late     its shift was already closed when it arrived — not in that Z
 *   price    lines sold at a price the owner changed while the till was offline
 *   promo    a promotion that had ended by the moment of the sale
 *   stock    the shelf held less than was sold (the stock stopped at 0)
 *   clock    the device's time was not believable; the arrival time was kept
 */
export type SyncFlags = {
  offline?: boolean
  late?: boolean
  price?: { name: string; sent: number; list: number }[]
  promo?: { name: string }[]
  stock?: { name: string; sold: number; had: number }[]
  clock?: string
}

const list = (v: unknown): Record<string, unknown>[] =>
  Array.isArray(v) ? v.filter((x): x is Record<string, unknown> => !!x && typeof x === 'object') : []
const num = (v: unknown) => (typeof v === 'number' ? v : Number(v) || 0)
const str = (v: unknown) => (v == null ? '?' : String(v))

/** The database's jsonb, read defensively. NULL, {} and anything else → null. */
export function parseSyncFlags(v: unknown): SyncFlags | null {
  if (!v || typeof v !== 'object' || Array.isArray(v)) return null
  const o = v as Record<string, unknown>
  const f: SyncFlags = {}
  if (o.offline === true) f.offline = true
  if (o.late === true) f.late = true
  const price = list(o.price).map((x) => ({ name: str(x.name), sent: num(x.sent), list: num(x.list) }))
  if (price.length) f.price = price
  const promo = list(o.promo).map((x) => ({ name: str(x.name) }))
  if (promo.length) f.promo = promo
  const stock = list(o.stock).map((x) => ({ name: str(x.name), sold: num(x.sold), had: num(x.had) }))
  if (stock.length) f.stock = stock
  if (typeof o.clock === 'string' && o.clock) f.clock = o.clock
  return Object.keys(f).length ? f : null
}

/** Something to look at beyond „it came later": a price, a promotion, the shelf, the clock. */
export function isOdd(f?: SyncFlags | null): boolean {
  return !!f && !!(f.price?.length || f.promo?.length || f.stock?.length || f.clock)
}

/** The one word a list row shows; null for an ordinary sale. */
export function syncTag(f?: SyncFlags | null): { key: Key; tone: 'muted' | 'amber' } | null {
  if (!f) return null
  if (f.late) return { key: 'sync.tagLate', tone: 'amber' }
  if (isOdd(f)) return { key: 'sync.tagOdd', tone: 'amber' }
  if (f.offline) return { key: 'sync.tagOffline', tone: 'muted' }
  return null
}

/** Every line the sale's detail says, in order; [] for an ordinary sale. */
export function syncNotes(f?: SyncFlags | null): { key: Key; params?: Record<string, string | number> }[] {
  if (!f) return []
  const out: { key: Key; params?: Record<string, string | number> }[] = []
  if (f.late) out.push({ key: 'sync.late' })
  for (const p of f.price ?? []) out.push({ key: 'sync.price', params: { name: p.name, sent: p.sent.toFixed(2), list: p.list.toFixed(2) } })
  for (const p of f.promo ?? []) out.push({ key: 'sync.promo', params: { name: p.name } })
  for (const s of f.stock ?? []) out.push({ key: 'sync.stock', params: { name: s.name, sold: s.sold, had: s.had } })
  if (f.clock) out.push({ key: 'sync.clock' })
  if (!out.length && f.offline) out.push({ key: 'sync.offline' })
  return out
}

export type LateSale = Pick<Sale, 'shift_id' | 'type' | 'total' | 'payment_method' | 'paid_cash' | 'paid_card' | 'paid_prepaid' | 'sync_flags'>
export type LateShiftTotals = { total: number; cash: number; card: number; credit: number; prepaid: number; count: number }

/** Per shift, what arrived after it closed, split by the canonical payment rails. */
export function lateByShift(sales: LateSale[]): Map<string, LateShiftTotals> {
  const m = new Map<string, LateShiftTotals>()
  for (const s of sales) {
    if (!s.shift_id || !s.sync_flags?.late) continue
    const cur = m.get(s.shift_id) ?? { total: 0, cash: 0, card: 0, credit: 0, prepaid: 0, count: 0 }
    const rails = saleRails(s)
    cur.cash = Math.round((cur.cash + rails.cash) * 100) / 100
    cur.card = Math.round((cur.card + rails.card) * 100) / 100
    cur.prepaid = Math.round((cur.prepaid + rails.prepaid) * 100) / 100
    cur.credit = Math.round((cur.credit + rails.credit) * 100) / 100
    cur.total = Math.round((cur.cash + cur.card + cur.credit + cur.prepaid) * 100) / 100
    cur.count += 1
    m.set(s.shift_id, cur)
  }
  return m
}
